import { supabase } from './supabase';

export interface Portfolio {
  id: string;
  user_id: string;
  template_id?: string;
  title: string;
  tagline?: string;
  bio?: string;
  slug?: string;
  email?: string;
  phone?: string;
  location?: string;
  website_url?: string;
  github_url?: string;
  linkedin_url?: string;
  twitter_url?: string;
  custom_links?: any[];
  custom_colors?: any;
  custom_fonts?: any;
  header_image?: string;
  profile_image?: string;
  is_public: boolean;
  published: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export interface PortfolioProject {
  id: string;
  portfolio_id: string;
  title: string;
  description?: string;
  long_description?: string;
  thumbnail_url?: string;
  role?: string;
  company?: string;
  project_type?: string;
  technologies?: string[];
  skills_used?: string[];
  live_url?: string;
  github_url?: string;
  start_date?: string;
  end_date?: string;
  is_ongoing: boolean;
  impact_metrics?: any[];
  featured_image?: string;
  gallery_images?: string[];
  video_url?: string;
  display_order: number;
  is_featured: boolean;
  is_visible: boolean;
}

export interface PortfolioTemplate {
  id: string;
  name: string;
  description?: string;
  preview_image?: string;
  layout_type: string;
  color_scheme: any;
  typography: any;
  features: string[];
  is_premium: boolean;
  display_order: number;
}

export interface PortfolioSkill {
  id: string;
  portfolio_id: string;
  skill_name: string;
  category?: string;
  proficiency_level: number;
  years_of_experience?: number;
  display_order: number;
  is_featured: boolean;
}

export interface PortfolioTestimonial {
  id: string;
  portfolio_id: string;
  name: string;
  role?: string;
  company?: string;
  avatar_url?: string;
  testimonial: string;
  rating?: number;
  project_id?: string;
  relationship?: string;
  date_given?: string;
  is_featured: boolean;
  is_approved: boolean;
}

export interface PortfolioSection {
  id: string;
  portfolio_id: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  content?: string;
  content_html?: string;
  layout: string;
  background_color?: string;
  background_image?: string;
  items?: any[];
  display_order: number;
  is_visible: boolean;
}

/**
 * Get all portfolio templates
 */
export async function getPortfolioTemplates(): Promise<PortfolioTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('portfolio_templates')
      .select('*')
      .eq('active', true)
      .order('display_order');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching portfolio templates:', error);
    return [];
  }
}

/**
 * Create a new portfolio
 */
export async function createPortfolio(
  userId: string,
  portfolio: Partial<Portfolio>
): Promise<Portfolio | null> {
  try {
    const { data, error } = await supabase
      .from('portfolios')
      .insert({
        user_id: userId,
        ...portfolio,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating portfolio:', error);
    return null;
  }
}

/**
 * Get user's portfolio
 */
export async function getUserPortfolio(userId: string): Promise<Portfolio | null> {
  try {
    const { data, error } = await supabase
      .from('portfolios')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user portfolio:', error);
    return null;
  }
}

/**
 * Get portfolio by slug (for public viewing)
 */
export async function getPortfolioBySlug(slug: string): Promise<Portfolio | null> {
  try {
    const { data, error } = await supabase
      .from('portfolios')
      .select('*')
      .eq('slug', slug)
      .eq('is_public', true)
      .eq('published', true)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching portfolio by slug:', error);
    return null;
  }
}

/**
 * Update portfolio
 */
export async function updatePortfolio(
  portfolioId: string,
  updates: Partial<Portfolio>
): Promise<Portfolio | null> {
  try {
    const { data, error } = await supabase
      .from('portfolios')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', portfolioId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating portfolio:', error);
    return null;
  }
}

/**
 * Publish/unpublish portfolio
 */
export async function togglePortfolioPublish(
  portfolioId: string,
  publish: boolean
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('portfolios')
      .update({
        published: publish,
        published_at: publish ? new Date().toISOString() : null,
      })
      .eq('id', portfolioId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error toggling portfolio publish:', error);
    return false;
  }
}

/**
 * Get portfolio projects
 */
export async function getPortfolioProjects(
  portfolioId: string
): Promise<PortfolioProject[]> {
  try {
    const { data, error } = await supabase
      .from('portfolio_projects')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .eq('is_visible', true)
      .order('display_order');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching portfolio projects:', error);
    return [];
  }
}

/**
 * Create portfolio project
 */
export async function createPortfolioProject(
  portfolioId: string,
  project: Partial<PortfolioProject>
): Promise<PortfolioProject | null> {
  try {
    const { data, error } = await supabase
      .from('portfolio_projects')
      .insert({
        portfolio_id: portfolioId,
        ...project,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating portfolio project:', error);
    return null;
  }
}

/**
 * Update portfolio project
 */
export async function updatePortfolioProject(
  projectId: string,
  updates: Partial<PortfolioProject>
): Promise<PortfolioProject | null> {
  try {
    const { data, error } = await supabase
      .from('portfolio_projects')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating portfolio project:', error);
    return null;
  }
}

/**
 * Delete portfolio project
 */
export async function deletePortfolioProject(projectId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('portfolio_projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting portfolio project:', error);
    return false;
  }
}

/**
 * Get portfolio skills
 */
export async function getPortfolioSkills(
  portfolioId: string
): Promise<PortfolioSkill[]> {
  try {
    const { data, error } = await supabase
      .from('portfolio_skills')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .order('display_order');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching portfolio skills:', error);
    return [];
  }
}

/**
 * Add portfolio skill
 */
export async function addPortfolioSkill(
  portfolioId: string,
  skill: Partial<PortfolioSkill>
): Promise<PortfolioSkill | null> {
  try {
    const { data, error } = await supabase
      .from('portfolio_skills')
      .insert({
        portfolio_id: portfolioId,
        ...skill,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding portfolio skill:', error);
    return null;
  }
}

/**
 * Get portfolio testimonials
 */
export async function getPortfolioTestimonials(
  portfolioId: string
): Promise<PortfolioTestimonial[]> {
  try {
    const { data, error } = await supabase
      .from('portfolio_testimonials')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .eq('is_approved', true)
      .order('display_order');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching portfolio testimonials:', error);
    return [];
  }
}

/**
 * Add portfolio testimonial
 */
export async function addPortfolioTestimonial(
  portfolioId: string,
  testimonial: Partial<PortfolioTestimonial>
): Promise<PortfolioTestimonial | null> {
  try {
    const { data, error } = await supabase
      .from('portfolio_testimonials')
      .insert({
        portfolio_id: portfolioId,
        ...testimonial,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding portfolio testimonial:', error);
    return null;
  }
}

/**
 * Get portfolio sections
 */
export async function getPortfolioSections(
  portfolioId: string
): Promise<PortfolioSection[]> {
  try {
    const { data, error } = await supabase
      .from('portfolio_sections')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .eq('is_visible', true)
      .order('display_order');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching portfolio sections:', error);
    return [];
  }
}

/**
 * Create portfolio section
 */
export async function createPortfolioSection(
  portfolioId: string,
  section: Partial<PortfolioSection>
): Promise<PortfolioSection | null> {
  try {
    const { data, error } = await supabase
      .from('portfolio_sections')
      .insert({
        portfolio_id: portfolioId,
        ...section,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating portfolio section:', error);
    return null;
  }
}

/**
 * Track portfolio visit
 */
export async function trackPortfolioVisit(
  portfolioId: string,
  visitorInfo?: {
    referrer?: string;
    deviceType?: string;
  }
): Promise<void> {
  try {
    await supabase.rpc('track_portfolio_visit', {
      p_portfolio_id: portfolioId,
      p_visitor_id: null,
      p_referrer: visitorInfo?.referrer || null,
      p_device_type: visitorInfo?.deviceType || 'desktop',
    });
  } catch (error) {
    console.error('Error tracking portfolio visit:', error);
  }
}

/**
 * Send contact message through portfolio
 */
export async function sendPortfolioContactMessage(
  portfolioId: string,
  message: {
    sender_name: string;
    sender_email: string;
    sender_phone?: string;
    sender_company?: string;
    subject?: string;
    message: string;
    regarding_project_id?: string;
  }
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('portfolio_contact_messages')
      .insert({
        portfolio_id: portfolioId,
        ...message,
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error sending contact message:', error);
    return false;
  }
}

/**
 * Get portfolio analytics summary
 */
export async function getPortfolioAnalytics(
  portfolioId: string,
  days: number = 30
): Promise<{
  totalViews: number;
  uniqueVisitors: number;
  topReferrers: string[];
  deviceBreakdown: { desktop: number; mobile: number; tablet: number };
}> {
  try {
    const { data, error } = await supabase
      .from('portfolio_analytics')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .gte('visited_at', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString());

    if (error) throw error;

    const analytics = data || [];

    return {
      totalViews: analytics.reduce((sum, a) => sum + a.page_views, 0),
      uniqueVisitors: new Set(analytics.map(a => a.visitor_id)).size,
      topReferrers: [...new Set(analytics.map(a => a.referrer).filter(Boolean))].slice(0, 5),
      deviceBreakdown: {
        desktop: analytics.filter(a => a.device_type === 'desktop').length,
        mobile: analytics.filter(a => a.device_type === 'mobile').length,
        tablet: analytics.filter(a => a.device_type === 'tablet').length,
      },
    };
  } catch (error) {
    console.error('Error fetching portfolio analytics:', error);
    return {
      totalViews: 0,
      uniqueVisitors: 0,
      topReferrers: [],
      deviceBreakdown: { desktop: 0, mobile: 0, tablet: 0 },
    };
  }
}

/**
 * Generate unique slug for portfolio
 */
export async function generatePortfolioSlug(
  userId: string,
  title: string
): Promise<string> {
  try {
    const { data, error } = await supabase.rpc('generate_portfolio_slug', {
      p_user_id: userId,
      p_title: title,
    });

    if (error) throw error;
    return data || title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  } catch (error) {
    console.error('Error generating portfolio slug:', error);
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }
}

/**
 * Upload media to portfolio
 */
export async function uploadPortfolioMedia(
  portfolioId: string,
  file: File,
  folder: string = 'general'
): Promise<string | null> {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${portfolioId}/${folder}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('portfolio-media')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('portfolio-media')
      .getPublicUrl(fileName);

    // Save to media library
    await supabase.from('portfolio_media').insert({
      portfolio_id: portfolioId,
      file_name: file.name,
      file_type: file.type.startsWith('image') ? 'image' : 'document',
      file_size: file.size,
      mime_type: file.type,
      storage_path: fileName,
      storage_url: publicUrl,
      folder,
    });

    return publicUrl;
  } catch (error) {
    console.error('Error uploading portfolio media:', error);
    return null;
  }
}
